/**
 * Reserves arrive physically (IDEAS X19; the ladder review's rule 1: a respawn is a reserve arriving). With
 * `MatchSetup.reserves`, a side's respawn tickets ARE its reserve — the units it held out of the fight, and
 * what supply brought — and a wave does not conjure a body at a spawn: the arrival comes in at the side's
 * **arrival region**, the lane it came by (the same region X22 leaves by), and walks in. A side with no
 * reserve left is out. Every arrival is logged with its index, so the sector can book which reserve unit
 * stepped in for which fallen body and what became of it. Pure; deterministic math only.
 */
import type { GameState, Vec3 } from '../types';
import { vecAdd, vecNormalize, vecScale, surfaceTangentFrame } from '../math';
import { dcos, dsin } from './dmath';
import { turnHeading } from './surfaceWalk';
import type { ExitRegion } from './withdrawal';
import { heldInstallations, landingRegionFor } from './installations';
import { INSTALLATIONS } from '../constants/body';
import { canRespawn } from './respawn';
import { dropRelays, dropHalfAngle, dropRefusal } from './drops';
import { structureTeam } from './body';
import { arcDistance } from './gameState';

export interface ReserveArrival { tick: number; teamIndex: number; playerId: number; /** The n-th reserve of its side to arrive (0-based): the sector's `reserveUnits[index]`. */ index: number }

/** Arrivals spread round the region's centre at this share of its half-angle, six to a ring. */
const ARRIVAL_RING = 0.5;

/** Where the `index`-th arrival of a side lands: inside its arrival region, spread round the centre so a wave does not stack. */
export function arrivalSpot(exit: ExitRegion, index: number): Vec3 {
  const n = vecNormalize(exit.facing);
  if (index === 0) return n;
  const ring = 1 + Math.floor((index - 1) / 6);
  const slot = (index - 1) % 6;
  const t = turnHeading(surfaceTangentFrame(n).tx, n, (slot * 2 * Math.PI) / 6);
  const theta = Math.min(exit.halfAngle * ARRIVAL_RING * ring, exit.halfAngle * 0.9);
  return vecNormalize(vecAdd(vecScale(n, dcos(theta)), vecScale(t, dsin(theta))));
}

/** How many reserves of a side have arrived so far. */
export function arrivalsOf(state: Pick<GameState, 'reserveArrivals'>, teamIndex: number): number {
  return (state.reserveArrivals ?? []).filter(a => a.teamIndex === teamIndex).length;
}

/**
 * Place a just-revived unit as its side's next reserve arriving: at its arrival region (the team's exit), logged
 * with its index. The caller has revived it (executeRespawn spends the ticket). Null when the team has no region.
 */
export function placeArrival(state: GameState, playerId: number, tick: number): GameState | null {
  const p = state.players.find(x => x.id === playerId);
  // X18 step 2: a held landing pad is where the reserves land; else the lane they came by
  const exit = p ? arrivalRegionFor(state, p.teamIndex) : undefined;
  if (!p || !exit) return null;
  const index = arrivalsOf(state, p.teamIndex);
  const surfaceDir = arrivalSpot(exit, index);
  return {
    ...state,
    players: state.players.map(q => (q.id === playerId ? { ...q, planetIndex: exit.planetIndex, surfaceDir } : q)),
    reserveArrivals: [...(state.reserveArrivals ?? []), { tick, teamIndex: p.teamIndex, playerId, index }],
  };
}

/**
 * 69.4 step 3: place a just-revived unit as its side's next reserve arriving at `spot` (under Operations: its linked FOB
 * or relay, or the push's landing — core/drops.ts pushArrivalAnchor), logged with its index as `placeArrival` logs it.
 */
export function placeArrivalAt(state: GameState, playerId: number, tick: number, spot: { planetIndex: number; surfaceDir: Vec3 }): GameState {
  const p = state.players.find(x => x.id === playerId);
  if (!p) return state;
  const index = arrivalsOf(state, p.teamIndex);
  return {
    ...state,
    players: state.players.map(q => (q.id === playerId ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir } : q)),
    reserveArrivals: [...(state.reserveArrivals ?? []), { tick, teamIndex: p.teamIndex, playerId, index }],
  };
}

/** A side's reserve still to come: its tickets (the reserve is the ticket pool under this rule). */
export function reserveLeft(state: Pick<GameState, 'modeState'>, teamIndex: number): number {
  return state.modeState.respawn?.tickets[teamIndex] ?? 0;
}

/**
 * What became of each arrival at the end: a body revived again later means that arrival died (it was replaced);
 * the last arrival of a body has the body's final standing.
 */
export function arrivalFates(state: Pick<GameState, 'players' | 'reserveArrivals'>): Array<ReserveArrival & { alive: boolean; health: number }> {
  const arrivals = state.reserveArrivals ?? [];
  return arrivals.map((a, i) => {
    const later = arrivals.slice(i + 1).some(b => b.playerId === a.playerId);
    const body = state.players.find(p => p.id === a.playerId);
    const alive = !later && !!body && (body.alive || !!body.extracted);
    return { ...a, alive, health: alive ? body!.health : 0 };
  });
}

/**
 * 68.4's open half (lane D): the reserve called from the commander's table. The places a side's reserve can land — its
 * lane (the exit it came by), every landing pad it holds (X18) and (69.3a) every relay of its that can take a drop — and
 * where the next arrival lands: the place the call chose while it still stands (a relay: while it can still take a drop),
 * else today's (the newest held pad, else the lane).
 */
export function arrivalPlaces(state: GameState, team: number): ExitRegion[] {
  const out: ExitRegion[] = [];
  const lane = state.exits?.[team];
  if (lane) out.push(lane);
  for (const pad of heldInstallations(state, team, 'landing')) out.push({ planetIndex: pad.planetIndex, facing: pad.surfaceDir, halfAngle: INSTALLATIONS.landingHalfAngle });
  if (state.config.drops) for (const r of dropRelays(state, team)) out.push({ planetIndex: r.planetIndex, facing: r.surfaceDir, halfAngle: dropHalfAngle(state, r), relayId: r.id });
  return out;
}

const samePlace = (a: ExitRegion, b: { planetIndex: number; facing: Vec3; relayId?: number }) => a.relayId === b.relayId && a.planetIndex === b.planetIndex && a.facing.x === b.facing.x && a.facing.y === b.facing.y && a.facing.z === b.facing.z;

export function arrivalRegionFor(state: GameState, team: number): ExitRegion | undefined {
  const chosen = state.reserveCalls?.[team]?.landAt;
  if (chosen) { const still = arrivalPlaces(state, team).find(r => samePlace(r, chosen)); if (still) return still; }
  return landingRegionFor(state, team) ?? state.exits?.[team];
}

/** The fallen of a side who could come in now (a ticket left for each, no cooldown), by id. */
export function reserveWaiting(state: GameState, team: number): number[] {
  const respawn = state.modeState.respawn;
  if (!respawn) return [];
  const out: number[] = [];
  let left = respawn.tickets[team] ?? 0;
  for (const p of [...state.players].sort((a, b) => a.id - b.id)) {
    if (p.alive || p.teamIndex !== team || p.extracted || left <= 0 || !canRespawn(p.id, team, respawn)) continue;
    out.push(p.id); left--;
  }
  return out;
}

/**
 * Apply a reserve order: refused without X19's reserves (the waves are respawns, not arrivals) or with no place to land.
 * `call` lifts a hold and makes the waiting due at once; with `at`, the reserve lands at the side's arrival place nearest
 * it from now on. `hold` keeps the fallen out of the waves until a call.
 */
export function applyReserveCall(state: GameState, team: number, call: 'hold' | 'call', at: { planetIndex: number; surfaceDir: Vec3 } | undefined, tick: number): { state: GameState } | { refused: string } {
  if (!state.reserveArrivals) return { refused: 'This battle has no reserve to call (its waves are respawns)' };
  const places = arrivalPlaces(state, team);
  if (!places.length) return { refused: 'The side has no place for its reserve to land' };
  // 69.3a: a call at one of the side's relays that can't take a drop (unlinked, spent, down, or in the sector in play) is refused
  if (call === 'call' && at && state.config.drops) {
    const planet = state.planets[at.planetIndex];
    const named = planet ? state.structures.find(s => s.type === 'relay' && s.planetIndex === at.planetIndex && structureTeam(state, s) === team
      && arcDistance(s.surfaceDir, at.surfaceDir, planet.radius) <= dropHalfAngle(state, s) * planet.radius) : undefined;
    const why = named ? dropRefusal(state, named) : null;
    if (why) return { refused: why };
  }
  const prev = state.reserveCalls?.[team] ?? { held: false, due: false };
  let next = { ...prev };
  if (call === 'hold') next = { ...prev, held: true, due: false };
  else {
    next = { ...prev, held: false, due: true, calledTick: tick };
    if (at) {
      let best = places[0], bestDot = -Infinity;
      for (const r of places) {
        if (r.planetIndex !== at.planetIndex) continue;
        const d = r.facing.x * at.surfaceDir.x + r.facing.y * at.surfaceDir.y + r.facing.z * at.surfaceDir.z;
        if (d > bestDot) { bestDot = d; best = r; }
      }
      next.landAt = { planetIndex: best.planetIndex, facing: best.facing, halfAngle: best.halfAngle, ...(best.relayId !== undefined ? { relayId: best.relayId } : {}) };
    }
  }
  return { state: { ...state, reserveCalls: { ...(state.reserveCalls ?? {}), [team]: next } } };
}

/** What the commander's card shows of a side's reserve: still to come (tickets), waiting to come in, held, and where it lands. */
export interface ReserveView { left: number; waiting: number; held: boolean; landsAt: 'lane' | 'pad' | 'relay' }
export function reserveView(state: GameState, team: number): ReserveView | null {
  if (!state.reserveArrivals) return null;
  const at = arrivalRegionFor(state, team);
  const lane = state.exits?.[team];
  return { left: reserveLeft(state, team), waiting: reserveWaiting(state, team).length, held: !!state.reserveCalls?.[team]?.held, landsAt: at?.relayId !== undefined ? 'relay' : at && lane && samePlace(at, lane) ? 'lane' : 'pad' };
}
